/**
 * ジョブ完了のブラウザ通知（Web Notification API）。
 *
 * 別タブ・別アプリ・ウィンドウ最小化中に生成ジョブが完了したことを OS の
 * デスクトップ通知で知らせる。発火点は notifyJobComplete() に集約しており、
 * 将来 Web Push (Service Worker) へ拡張する際もここを起点にできる。
 *
 * 詳細設計: docs/requirements/job-completion-browser-notification-plan.md
 */

import { brandConfig } from '../config/brands';

const STORAGE_KEY_NOTIFY_ENABLED = 'job_notification_enabled';

/** type 別の通知タイトル */
const TITLE_BY_TYPE: Record<string, string> = {
  image: '画像の生成が完了しました',
  video: '動画の生成が完了しました',
  'scene-preview': 'シーン画像の生成が完了しました',
  'concat-video': '動画の書き出しが完了しました',
  storyboard: '絵コンテの生成が完了しました',
  scenario: 'シナリオの生成が完了しました',
  'video-edit-propose': 'AI編集の提案が完了しました',
  'video-edit-analyze': 'シーンの解析が完了しました',
  'video-edit-render': 'MP4の書き出しが完了しました',
};
const DEFAULT_TITLE = '生成が完了しました';

export type JobNotificationPayload = {
  /** ActiveJob.type 相当。未指定時は汎用文面 */
  type?: string;
  /** タイトルを明示指定したい場合（未指定なら type から決定） */
  title?: string;
  /** 本文（ジョブのラベル等）。未指定なら省略 */
  body?: string;
  /** 通知の tag に使い、同一ジョブの重複表示を抑止する */
  jobId: string;
};

/** このブラウザが Notification API に対応しているか */
export function isNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * モバイル OS（iOS / Android）かどうか。画面幅ではなく OS で判定する。
 *
 * 完了通知は「ページ内ポーリングが完了を検知して new Notification() を出す」
 * ローカル通知方式のため、発火には完了時にページの JS が動いている必要がある。
 * モバイルはアプリ切替・画面ロックでバックグラウンド JS が凍結され、肝心の
 * バックグラウンド時に検知できないため、OS 単位で完了通知を提供しない。
 * (真にモバイルで出すには Web Push + Service Worker が必要。別途実装)
 *
 * - iOS: Safari 通常タブに Notification API が無く isNotificationSupported() で
 *   既に除外されるが、念のためここでも判定する。なお iPadOS は UA が Mac を
 *   詐称するため UA 正規表現には掛からないが、API 非対応で自動的に除外される。
 * - Android: スマホ/タブレットとも UA に "Android" を含むため両方除外できる。
 */
export function isMobileOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const uaData = (navigator as { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile) return true; // Chromium のモバイル判定（高信頼）
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

/** ユーザーが通知を ON にしているか（localStorage 永続化） */
export function getNotificationPreference(): boolean {
  if (!isNotificationSupported()) return false;
  try {
    return localStorage.getItem(STORAGE_KEY_NOTIFY_ENABLED) === 'true';
  } catch {
    return false;
  }
}

/** 通知の ON/OFF を保存 */
export function setNotificationPreference(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_NOTIFY_ENABLED, String(enabled));
  } catch {
    // 書き込み失敗は無視
  }
}

/** 現在の許可状態 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * 通知許可をリクエストする。必ずユーザー操作（クリック等）起点で呼ぶこと。
 * 許可が得られたら true。非対応・拒否時は false。
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNotificationSupported()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

/**
 * フォアグラウンドかつフォーカス中か。
 * この場合は画面上で完了が分かる（toast が出る）ため通知を抑制する。
 */
function isAppInForeground(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * ジョブ完了通知の唯一の発火点。
 *
 * 以下のいずれかに該当する場合は何もしない:
 *   - 非対応ブラウザ / 設定 OFF / 許可なし
 *   - フォアグラウンドかつフォーカス中（toast と二重にしない）
 *
 * 将来 Web Push へ拡張する場合は、ここで Service Worker 経由の送信に
 * 差し替える（ただし購読保存・サーバー側 push 送信は別途必要）。
 */
export function notifyJobComplete(payload: JobNotificationPayload): void {
  if (!isNotificationSupported()) return;
  if (isMobileOS()) return;
  if (!getNotificationPreference()) return;
  if (Notification.permission !== 'granted') return;
  if (isAppInForeground()) return;

  const title = payload.title ?? TITLE_BY_TYPE[payload.type ?? ''] ?? DEFAULT_TITLE;

  try {
    const notification = new Notification(title, {
      body: payload.body,
      // 同一ジョブで通知が重複しても 1 件に集約される
      tag: payload.jobId,
      // ブランド別 favicon を使う。ここを '/favicon.ico' 固定にすると、SAITE 等の
      // パートナードメインでも default (商品上品) のロゴで通知が出てしまう
      // (favicon.ico は全ブランド共通で配信される default のアイコン)。
      icon: brandConfig.favicon ?? `${import.meta.env.BASE_URL}favicon.ico`,
    });
    // クリックでアプリのタブ／ウィンドウを前面に出す（フェーズ1はフォーカスのみ）
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // 通知生成失敗は致命的ではないので握りつぶす
  }
}
